import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {Paper, Typography} from '@material-ui/core';
import {clearCanvas} from '../../services/canvasService';

const styles = ({spacing, mixins}) => {
    return {
        numberDisplay: {
            position: 'fixed',
            top: spacing(2),
            paddingTop: spacing(),
            paddingBottom: spacing(),
            ...mixins.gutters(),
            zIndex: 10,
        },
        positionLeft: {
            left: spacing(2),
            right: 'auto',
        },
        positionRight: {
            left: 'auto',
            right: spacing(2),
        },
        highlightCanvas: {position: 'absolute', top: -1, left: -1, cursor: 'none'},
    }
};

const CONST = {
    positions: {
        left: 'left',
        right: 'right',
    },
};

class NumberUnderPointerDisplay extends Component {
    constructor(props) {
        super(props);
        this.numberDisplayRef = React.createRef();
        this.highlightCanvasRef = React.createRef();
        this.state = {
            mousePositionOnCanvas: null,
            numberDisplayPosition: CONST.positions.right,
        };
    }

    render() {
        const {classes, canvasWidth, canvasHeight} = this.props;

        return (
            <>
                <canvas
                    className={classes.highlightCanvas}
                    ref={this.highlightCanvasRef}
                    width={canvasWidth + 2}
                    height={canvasHeight + 2}
                    onMouseEnter={this.updateNumberUnderPointer}
                    onMouseMove={this.updateNumberUnderPointer}
                    onMouseLeave={this.clearNumberDisplay}
                />
                {this.renderNumberDisplay()}
            </>
        );
    }

    renderNumberDisplay = () => {
        const {classes} = this.props;
        const {mousePositionOnCanvas, numberDisplayPosition} = this.state;

        if (mousePositionOnCanvas === null) return null;

        const number = this.calculateNumberUnderPointer();
        const positionClass = (numberDisplayPosition === CONST.positions.left ? classes.positionLeft : classes.positionRight);

        return (
            <Paper
                ref={this.numberDisplayRef}
                className={[classes.numberDisplay, positionClass].join(' ')}
                elevation={1}
                onMouseEnter={this.togglePosition}
            >
                <Typography variant="caption">
                    Number under pointer:
                </Typography>
                <Typography variant="h5">
                    {number}
                </Typography>
            </Paper>
        )
    }

    updateNumberUnderPointer = (event) => {
        if (this.numberDisplayRef.current !== null && this.highlightCanvasRef.current !== null) {
            const displayDimensions = this.numberDisplayRef.current.getBoundingClientRect();
            const {bottom, right, left} = displayDimensions;
            const middle = (right - left) / 2;
            const elementAtXY = document.elementFromPoint(middle, bottom + 1);

            if (!elementAtXY.isSameNode(this.highlightCanvasRef.current)) {
                this.setState({
                    numberDisplayPosition: CONST.positions.right,
                });
            }
        }

        const {numbersPerRow, pixelsPerNumber} = this.props;

        const ctx = this.highlightCanvasRef.current.getContext('2d');
        const number = this.calculateNumberUnderPointer(event.nativeEvent.offsetX, event.nativeEvent.offsetY) - 1;
        const startX = number % numbersPerRow * pixelsPerNumber + 0.5;
        const startY = Math.floor(number / numbersPerRow) * pixelsPerNumber + 0.5;
        const size = pixelsPerNumber + 1;

        clearCanvas(this.highlightCanvasRef);
        ctx.strokeStyle = 'black';
        ctx.lineJoin = 'miter';
        ctx.lineWidth = 1;
        ctx.strokeRect(startX, startY, size, size);

        const mousePositionOnCanvas = {
            x: event.nativeEvent.offsetX,
            y: event.nativeEvent.offsetY,
        };

        this.setState({
            mousePositionOnCanvas,
        });
    };

    clearNumberDisplay = () => {
        clearCanvas(this.highlightCanvasRef);

        this.setState({
            mousePositionOnCanvas: null,
        });
    };

    togglePosition = () => {
        const {numberDisplayPosition} = this.state;
        let newPosition = (numberDisplayPosition === CONST.positions.left ? CONST.positions.right : CONST.positions.left);

        this.setState({
            numberDisplayPosition: newPosition,
        });
    }

    // highlight canvas is 2 pixels wider and 2 pixels higher than the numbers canvas
    calculateNumberUnderPointer = (x = this.state.mousePositionOnCanvas.x, y = this.state.mousePositionOnCanvas.y) => {
        const {numbersPerRow, pixelsPerNumber, canvasWidth, canvasHeight} = this.props;

        let normX;
        let normY;

        if (x < 0) {
            normX = 0;
        } else if (x > canvasWidth) {
            normX = canvasWidth;
        } else {
            normX = x;
        }

        if (y < 0) {
            normY = 0;
        } else if (y > canvasHeight) {
            normY = canvasHeight;
        } else {
            normY = y;
        }

        let columnIndex = Math.floor(normX / pixelsPerNumber);
        if (columnIndex >= numbersPerRow) {
            columnIndex = numbersPerRow - 1;
        }

        let rowIndex = Math.floor(normY / pixelsPerNumber);
        const maxRowsCount = canvasHeight / pixelsPerNumber;
        if (rowIndex > maxRowsCount - 1) {
            rowIndex = maxRowsCount - 1;
        }

        return rowIndex * numbersPerRow + columnIndex + 1;
    }
}

NumberUnderPointerDisplay.propTypes = {
    classes: PropTypes.object.isRequired,
    numbersPerRow: PropTypes.number.isRequired,
    pixelsPerNumber: PropTypes.number.isRequired,
    canvasWidth: PropTypes.number.isRequired,
    canvasHeight: PropTypes.number.isRequired,


};

export default withStyles(styles)(NumberUnderPointerDisplay);
